import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProductRecordForm } from "./ProductRecordForm";
import { useProductRecord, useUpdateProductRecord } from "../../hooks/useProductRecords";
import { useWorkspaceUrl } from "../../lib/routes";
import { productErrorKey } from "../../lib/productRecords";

/** The review dialog of one record: loads it, and sends Save or Approve to the server. */
export function ProductRecordReview({ projectId, recordId, onClose }: { projectId: string; recordId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const urls = useWorkspaceUrl();
  const { data: record, isError, error: loadError } = useProductRecord(recordId);
  const update = useUpdateProductRecord(projectId, recordId);

  const submit = (patch: Parameters<typeof update.mutate>[0]) =>
    update.mutate(patch, {
      onError: (error) => console.error("Failed to update product record", { recordId, error: error.message }),
    });

  useEffect(() => {
    if (isError) console.error("Failed to load product record", { recordId, error: (loadError as Error).message });
  }, [isError, loadError, recordId]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("productCatalog.review.title")}</DialogTitle>
        </DialogHeader>
        {isError && (
          <p role="alert" className="text-sm text-red-600">
            {t(productErrorKey(loadError, "productCatalog.errors.loadFailed"))}
          </p>
        )}
        {!record && !isError && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
        {record && (
          <ProductRecordForm
            // A saved record is the new starting point: the form starts again from it.
            key={`${record.id}-${record.updated_at}`}
            record={record}
            documentHref={urls ? urls.document(projectId, record.document_id) : null}
            busy={update.isPending}
            error={update.error}
            onSubmit={submit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
